import { useCallback, useMemo } from "react";
import { Chart, ChartTypes } from "@uicore";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  NativeSelect,
  Skeleton,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Bar } from "recharts";
import { AiMlServicesTrendPoint } from "../types";
import { formatCost } from "@/helpers/utils";
import { type Granularity, formatDate, SERVICE_COLORS } from "../utils";

interface Props {
  data: AiMlServicesTrendPoint[];
  isLoading?: boolean;
  currency: string;
  granularity: Granularity;
  onGranularityChange: (granularity: Granularity) => void;
}

type BarRadius = [number, number, number, number];

const SERIES: { key: string; fill: string; radius: BarRadius }[] = [
  { key: "Model Serving", fill: SERVICE_COLORS.model_serving, radius: [0, 0, 0, 0] },
  { key: "Vector Search", fill: SERVICE_COLORS.vector_search, radius: [0, 0, 0, 0] },
  { key: "AI Gateway", fill: SERVICE_COLORS.ai_gateway, radius: [4, 4, 0, 0] },
];

export const TrendChart = ({
  data,
  isLoading,
  currency,
  granularity,
  onGranularityChange,
}: Props) => {
  const formatCurrencyValue = useCallback(
    (value: number) => formatCost(value, currency),
    [currency]
  );

  const chartData = useMemo(
    () =>
      data.map(point => ({
        period: point.date,
        "Model Serving": point.model_serving_cost_usd,
        "Vector Search": point.vector_search_cost_usd,
        "AI Gateway": point.ai_gateway_cost_usd,
      })),
    [data]
  );

  return (
    <Card>
      <CardHeader>
        <Stack direction="row" align="center" justify="between" className="al-w-full">
          <Typography variant="section-heading">Total AI/ML Services Cost</Typography>
          <Stack direction="row" align="center" gap="xs">
            <Typography size="sm" className="al-text-gray-500">
              Aggregate by
            </Typography>
            <NativeSelect
              aria-label="Select granularity"
              value={granularity}
              onChange={(value: string) => {
                const valid: Granularity[] = ["day", "week", "month"];
                if (valid.includes(value as Granularity)) {
                  onGranularityChange(value as Granularity);
                }
              }}
              variant="outline"
              size="sm"
              options={[
                { value: "day", label: "Daily" },
                { value: "week", label: "Weekly" },
                { value: "month", label: "Monthly" },
              ]}
            />
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent>
        <div className="al-pb-10">
          {isLoading ? (
            <Skeleton className="al-h-[380px] al-w-full" />
          ) : (
            <Chart
              title=""
              xAxisDataKey="period"
              yAxisLabel={{ value: "Cost" }}
              xAxisLabelFormatter={(value: string) => formatDate(value, granularity)}
              yAxisLabelFormatter={(value: string) => formatCurrencyValue(Number(value))}
              type={ChartTypes.BarChart}
              data={chartData}
              height={380}
              legendProps={{ wrapperStyle: { bottom: -16, paddingTop: 4, fontSize: 12 } }}
            >
              {({ barSize, selectedLegend }) => (
                <>
                  {SERIES.map(series => (
                    <Bar
                      key={series.key}
                      barSize={barSize}
                      name={series.key}
                      dataKey={series.key}
                      stackId="service-cost"
                      fill={series.fill}
                      radius={series.radius}
                      hide={Boolean(selectedLegend && selectedLegend !== series.key)}
                    />
                  ))}
                </>
              )}
            </Chart>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
